import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Mapping from "../../../../Common/Mapping/Mapping";
import { ButtonUI } from "../../../../Common";

import Workspace from "../../../../../service/workspace";
import { showToast } from "../../../../../store/actions";
import { identifyDataTypeAndFormat, setPrevState } from "../../../../../util/common";
import { checkReservedKeywords } from "../../../../../util/restrictedStatement";

const MappingPopup = ({
  destinationType,
  exchangeJobState,
  setExchangeJobState,
  delimeter,
  nullValue,
  isFilePropertiesChanged,
  updatePreviousRef,
  existingFields,
}) => {
  const { attributeDataTypes, encodingTypes, previewCount = 100 } = useSelector((state) => state.config);

  const { options, formats, types } = useMemo(() => {
    const options = {};
    const formats = {};
    const types = [];

    attributeDataTypes.forEach((option) => {
      if (option.options) {
        if (option.type !== "Date" && option.type !== "DateTime" && option.type !== "Time")
          options[option.type] = option.options.map(({ SqlValue, value }) => ({
            value: destinationType === "mysql" && SqlValue ? SqlValue : value,
          }));
        else
          formats[option.type] = option.options.map(({ SqlValue, value }) => ({
            value: destinationType === "mysql" && SqlValue ? SqlValue : value,
          }));
      }

      if (option.type !== "Reference")
        types.push({
          value: option.type,
          text: option.type,
        });
    });

    return { options, formats, types };
  }, [attributeDataTypes, destinationType]);

  const {
    bucket,
    destination,
    destinationAction,
    fieldEnclosedBy,
    encoding,
    fileType,
    hasHeaderRow,
    source: location,
    sourcePath: path,
    schemaName,
    tableName,
    existingTableAction,
    sourceType: type,
    source,
    sourcePath,
    groupBy,
    cluster = "",
  } = useMemo(() => exchangeJobState, [exchangeJobState]);

  const append = destinationAction === "existing" && existingTableAction === "append";
  const dispatch = useDispatch();
  const { Workspace_id } = useParams();

  const [state, setState] = useState({
    validMapping: false,
    loading: false,
  });
  const [columns, setColumns] = useState({
    headers: [],
    mappings: [],
    data: [],
    fileName: "",
  });

  const [scrollToIndex, setScrollToIndex] = useState(null);

  const getS3FileColumns = useCallback(async () => {
    const workspace = new Workspace();

    let bucketName = "";
    let file = "";

    if (type.toLowerCase() === "s3") {
      //Setting default values based on url props
      const bucketNameArray = bucket.replace("s3://", "").split("/");

      bucketName = bucketNameArray[0];
      bucketNameArray.shift();

      file = bucketNameArray.length ? bucketNameArray.join("/") : "";
      if (bucketNameArray.length && file && !file.endsWith("/")) file += "/";

      //Checking if path has file selected
      if (path) {
        if (file.endsWith("/")) file = file + path.substring(1);
        else file = file + path;
      }
    } else file = path;

    const response = await workspace.loadFileColumns(Workspace_id, {
      bucket: bucketName,
      file,
      delimiter: delimeter,
      nullValue,
      fieldEnclosedBy,
      encoding: encodingTypes.find((en) => en.value === encoding).text ?? "UTF-8", //provide text,
      hasHeaderRow,
      location,
      destination,
      append,
      database: groupBy,
      cluster,
      schemaName,
      tableName,
      fileType,
      previewCount,
    });

    if (response.success) {
      if (response.data) response.data = JSON.parse(response.data);

      let columnsDataTypesAndFormat = {};
      if (response.data.sampleData.length > 0) {
        columnsDataTypesAndFormat = identifyDataTypeAndFormat(
          response.data.fields,
          response.data.sampleData,
          destinationType
        );
      } else {
        if (response.data.snowflakeFields) {
          response.data.snowflakeFields?.forEach(({ name, size, type: dataType }) => {
            columnsDataTypesAndFormat[name] = {
              dataType,
              size,
              format: "",
            };
          });
        } else {
          response.data.fields?.forEach((field) => {
            columnsDataTypesAndFormat[field] = {
              dataType: "String",
              format: null,
            };
          });
        }
      }

      const isUnchanged = !isFilePropertiesChanged && existingFields.length;

      setColumns({
        fileName: response.data.fileName || "",
        headers: response.data.fields,
        error: response.data.error || false,
        mappings: response.data.fields.map((field, i) => {
          if (existingTableAction !== "overwrite" && isUnchanged && i < existingFields.length) {
            const newObj = { ...existingFields[i] };
            newObj.isSelected = false;
            newObj.source = field;
            return newObj;
          }

          const _field = columnsDataTypesAndFormat[field] || columnsDataTypesAndFormat[field.toUpperCase()];

          const dataType = _field.dataType;
          let format = "";
          let customFormat = "";
          if (formats[dataType] !== undefined) {
            let recivedFormat = _field.format;
            if (recivedFormat !== undefined && recivedFormat !== null) {
              format = formats[dataType].find((el) => el.value === _field.format);
              if (format === undefined) {
                format = { value: "Custom" };
                customFormat = _field.format;
              }
            }
          }
          let destinationName = HandleFields(append ? response.data.snowflakeFields[i].name : field);
          let hasError = false;
          let errorMessage = "";

          if (checkReservedKeywords(destinationName, destinationType)) {
            hasError = true;
            errorMessage = "Reserved keyword";
          } else if (destinationName.length > 128) {
            hasError = true;
            errorMessage = "Max limit: 128 Characters!";
          }

          let size = _field.length;
          if (append && response.data.snowflakeFields[i].size) size = response.data.snowflakeFields[i].size;
          if (!size) {
            const currOptions = options[dataType];
            if (currOptions) {
              const index = dataType === "Number" ? 0 : dataType === "String" ? 5 : currOptions.length - 1;
              size = currOptions[index].value;
            } else size = "";
          }

          return {
            source: field,
            destination: destinationName,
            type: append ? response.data.snowflakeFields[i].type : dataType,
            size,
            format: format ? format.value : "",
            customFormat: format && format.value === "Custom" ? customFormat : "",
            hasError,
            errorMessage,
            isSelected: false,
          };
        }),
        data: response.data.sampleData,
      });

      setPrevState(setState, { validMapping: true });
    } else {
      setPrevState(setState, { validMapping: false, loading: false });
      setPrevState(setExchangeJobState, {
        mapping: false,
      });
    }
  }, [
    type,
    path,
    Workspace_id,
    delimeter,
    nullValue,
    fieldEnclosedBy,
    encodingTypes,
    hasHeaderRow,
    location,
    destination,
    append,
    groupBy,
    cluster,
    schemaName,
    tableName,
    fileType,
    previewCount,
    bucket,
    encoding,
    isFilePropertiesChanged,
    existingFields,
    destinationType,
    existingTableAction,
    formats,
    options,
    setExchangeJobState,
  ]);

  const HandleFields = (field) => {
    // Replace space with underscore(_) if field name contains space( )
    field = field.trim().replaceAll(/[^a-zA-Z0-9_$]/gi, "_");

    // Replace first chart with underscore(_) If field Name is not starting with Alphabet or underscore
    const regEx = /^[a-zA-Z_].*/;
    if (!regEx.test(field)) field = `_${field}`;

    return field;
  };

  const updateMapping = useCallback(
    (value, source) => {
      if (value["destination"]) value["destination"] = value["destination"].replaceAll(" ", "");

      setColumns((prev) => {
        return {
          ...prev,
          mappings: prev.mappings.map((_mapping) =>
            _mapping.source === source
              ? {
                  ..._mapping,
                  ...value,
                  ...(value.type && _mapping.type !== value.type
                    ? {
                        size: options[value.type]
                          ? options[value.type][
                              value.type === "Number" ? 0 : value.type === "String" ? 5 : options[value.type].length - 1
                            ].value
                          : "",
                        format: formats[value.type] ? formats[value.type][formats[value.type].length - 1].value : "",
                        customFormat: "",
                      }
                    : {}),
                }
              : _mapping
          ),
        };
      });
    },
    [formats, options]
  );

  useEffect(() => {
    (async () => {
      getS3FileColumns();
    })();

    return () => {
      setState({
        validMapping: false,
        loading: false,
      });
      setPrevState(setExchangeJobState, {
        mapping: false,
      });
      setColumns({
        headers: [],
        mappings: [],
        data: [],
        error: false,
      });
    };
  }, [getS3FileColumns, setExchangeJobState]);

  //When Preview popup is opened then auto scroll to first reserved keyword if found
  useEffect(() => {
    if (state.validMapping) {
      let scrollIndex = null;
      columns.mappings.forEach(({ hasError }, index) => {
        if (hasError) scrollIndex = index;
      });
      if (scrollIndex) setScrollToIndex(scrollIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.validMapping]);

  //Validate Destination Columns string which allows alphanumeric, underscore and doller
  const validateDestinationCols = (colMappings) => {
    let valid = false;

    //Find all reserved keywords and auto scroll to first reserved keyword if found
    let scrollIndex = null;
    let reservedFlags = columns.mappings.filter(({ source, destination, hasError }, index) => {
      const isReserved = checkReservedKeywords(destination, destinationType);
      if (isReserved && scrollIndex === null) scrollIndex = index;
      if (isReserved !== hasError)
        updateMapping({ hasError: isReserved, errorMessage: isReserved ? "Reserved keyword" : "" }, source);
      return isReserved;
    });
    if (scrollIndex) setScrollToIndex(scrollIndex);

    //Validating unique columns
    const uniqueColumns = new Set(colMappings.map(({ destination }) => destination.toLowerCase()));
    if (colMappings.length !== uniqueColumns.size)
      dispatch(
        showToast({
          message: "Destination columns are not unique!",
          type: "error",
        })
      );
    else if (colMappings.some((column) => !column.destination.trim()))
      dispatch(
        showToast({
          message: "Destination columns can not be empty!",
          type: "error",
        })
      );
    else if (reservedFlags.length)
      dispatch(
        showToast({
          message: `${reservedFlags.length} destination column${
            reservedFlags.length > 1 ? "s" : ""
          } contain reserved keyword${reservedFlags.length > 1 ? "s" : ""}!`,
          type: "error",
        })
      );
    else if (colMappings.some((column) => column.destination.length > 128)) {
      let scrollIndex = 0;
      setColumns((prev) => {
        return {
          ...prev,
          mappings: prev.mappings.map((mapping, i) => {
            const hasError = mapping.destination.length > 128;
            if (!scrollIndex && hasError) {
              scrollIndex = i;
              setScrollToIndex(scrollIndex);
            }

            return mapping.destination.length > 128
              ? { ...mapping, hasError: true, errorMessage: "Max limit: 128 Characters!" }
              : mapping;
          }),
        };
      });
      dispatch(
        showToast({
          message: "Length of the Destination Column should be less than 128!",
          type: "error",
        })
      );
    } else if (
      append &&
      destination === "SNOWFLAKE" &&
      columns.mappings.some(
        (column) =>
          column.size &&
          options[column.type] &&
          !options[column.type].some(({ value }) => parseInt(value) === parseInt(column.size))
      )
    ) {
      dispatch(
        showToast({
          message: "Error while processing, please check the column size!",
          type: "error",
        })
      );
    } else if (
      columns.mappings.some(
        (column) =>
          column.format &&
          formats[column.type] &&
          column.format === "Custom" &&
          (column.customFormat === "" || column.customFormat === null || column.customFormat === undefined)
      )
    ) {
      dispatch(
        showToast({
          message: "Custom format cant't be empty, please provide custom format",
          type: "error",
        })
      );
    } else valid = true;

    return valid;
  };

  const handleSubmit = async () => {
    if (!validateDestinationCols(columns.mappings)) return;
    else {
      setPrevState(setExchangeJobState, { fields: columns.mappings, mapping: false, isFieldsConfigured: true });
      updatePreviousRef({
        destination: destination,
        delimiter: delimeter,
        nullValue,
        hasHeaderRow: hasHeaderRow,
        encoding: encoding,
        fieldEnclosedBy: fieldEnclosedBy,
        fields: columns.mappings,
        schemaName: schemaName,
        tableName: tableName,
        destinationAction: destinationAction,
        existingTableAction: existingTableAction,
        source: source,
        sourcePath: sourcePath,
      });
      dispatch(
        showToast({
          message: "Table configurations saved successfully!",
          type: "success",
        })
      );
    }
  };

  return (
    <Mapping
      handleClose={() => {
        setPrevState(setExchangeJobState, {
          mapping: false,
        });
      }}
      validMapping={state.validMapping}
      dataTypes={types}
      updateMapping={updateMapping}
      loading={state.loading}
      append={append}
      options={options}
      columns={columns}
      setColumns={setColumns}
      scrollToIndex={scrollToIndex}
      formats={formats}
      btnGroup={
        <ButtonUI
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleSubmit()}
          variant="contained"
          color="success"
          style={{ marginRight: 15 }}
          disabled={state.loading}
        >
          Save Configurations
        </ButtonUI>
      }
      destinationType={destinationType}
    />
  );
};

export default React.memo(MappingPopup);
